import { useEffect, useRef, useState } from 'react';
import { Drawer } from '@mui/material';

import NotificationList from './NotificationList';
import AlarmDialog from '../alarms/AlarmDialog';

import {
  removeAlarm,
  removeAllAlarms,
  removeTimeWarningAlarm,
  selectAlarms,
} from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { SocketAlarm } from '../spectrasWebSocketServer/types';

const drawerWidth = 250;

interface Props {
  open: boolean;
  handleClose: () => void;
}

const NotificationDrawer = ({ open, handleClose }: Props) => {
  const dispatch = useAppDispatch();

  const alarms: SocketAlarm[] = useAppSelector(selectAlarms);
  const user = useAppSelector((state) => state.auth.user);
  const [alarmDialogOpen, setAlarmDialogOpen] = useState(false);
  const [selectedAlarm, setSelectedAlarm] = useState<null | SocketAlarm>(null);
  const priorAlarmCount = useRef(0);

  useEffect(() => {
    if (!user || !alarms || alarms.length < 1) {
      priorAlarmCount.current = 0;
      return;
    }
    if (priorAlarmCount.current < alarms.length && user.alarm_popups) {
      setSelectedAlarm(alarms[alarms.length - 1]);
      setAlarmDialogOpen(true);
      priorAlarmCount.current = alarms.length;
    } else {
      priorAlarmCount.current = alarms.length;
    }
  }, [user, alarms]);

  const handleAlarmClicked = (alarm: SocketAlarm) => {
    setSelectedAlarm(alarm);
    setAlarmDialogOpen(true);
  };

  const handleAlarmDeleted = (index: number) => {
    dispatch(removeAlarm(index));
  };

  const handleTimeWarningAlarmDeleted = (index: number) => {
    dispatch(removeTimeWarningAlarm(index));
  };

  const handleAllAlarmsDeleted = () => {
    dispatch(removeAllAlarms());
  };

  return (
    <>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          zIndex: 99999,
          pointerEvents: alarmDialogOpen ? 'none' : 'auto',
        }}
        PaperProps={{
          sx: {
            width: drawerWidth,
          },
        }}
        anchor={'right'}
        open={open}
        onClose={handleClose}
        BackdropProps={{ invisible: alarmDialogOpen ? true : false }}
        ModalProps={{
          onClose: (event: object, reason: string) => {
            if (alarmDialogOpen) return;
            handleClose();
          },
          disableEnforceFocus: true,
        }}
      >
        <NotificationList
          handleAlarmClicked={handleAlarmClicked}
          handleAlarmDeleted={handleAlarmDeleted}
          handleAllAlarmsDeleted={handleAllAlarmsDeleted}
          handleTimeWarningAlarmDeleted={handleTimeWarningAlarmDeleted}
        />
      </Drawer>
      <AlarmDialog
        alarm={selectedAlarm}
        open={alarmDialogOpen}
        handleClose={() => setAlarmDialogOpen(false)}
        handleNotificationDrawerClose={handleClose}
      />
    </>
  );
};

export default NotificationDrawer;
